import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { TokenMark } from "@/components/Brand";
import {
  ASQAmount,
  EmptyState,
  Skeleton,
  Status,
  utilityLabel,
} from "@/components/primitives";
import { api, type Utility } from "@/lib/api";
import { daysUntil, formatASQ, formatDate, shortAddress } from "@/lib/format";

export function DashboardPage() {
  const { address, isConnected } = useAccount();

  const { data, isPending } = useQuery({
    queryKey: ["dashboard", address],
    queryFn: () => api.dashboard(address!),
    enabled: Boolean(address),
    refetchInterval: 30_000,
  });

  if (!isConnected) {
    return (
      <div className="sheet route-page">
        <EmptyState
          title="Hubungkan wallet untuk membuka ringkasan"
          action={
            <Link to="/membership" className="btn btn--ghost">
              Lihat utility terlebih dahulu
            </Link>
          }
        >
          Saldo ASQ, membership, vault utility, dan riwayat transaksi ditampilkan
          setelah wallet terhubung.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="sheet route-page dashboard-workspace">
      <header className="workspace-heading">
        <div>
          <p className="workspace-heading__context">{greeting()} · Utility console</p>
          <h1 className="display-section">Ringkasan wallet</h1>
          <p>Saldo yang siap digunakan, status akses, dan jejak ASQ di seluruh utility.</p>
        </div>
        <span className="wallet-address datum">{shortAddress(address)}</span>
      </header>

      <section className="wallet-ledger">
        <div className="wallet-ledger__balance">
          <div className="wallet-ledger__coin" aria-hidden="true">
            <TokenMark size={260} decorative />
          </div>
          <div className="wallet-ledger__balance-content">
            <p>Saldo ASQ tersedia</p>
            {isPending ? (
              <Skeleton className="mt-4 h-20 w-72" />
            ) : (
              <p className="mt-4">
                <ASQAmount raw={data?.balanceRaw} size="xl" maxFractionDigits={4} />
              </p>
            )}
            <p className="wallet-ledger__balance-note">
              Gunakan saldo ini untuk mengaktifkan akses atau membayar utility properti.
            </p>
          </div>

          <nav className="dashboard-actions" aria-label="Aksi cepat">
            <Link to="/membership">
              <span>Membership</span>
              <strong>Aktifkan akses →</strong>
            </Link>
            <Link to="/vouchers">
              <span>E-Voucher</span>
              <strong>Tukar benefit →</strong>
            </Link>
            <Link to="/catalog">
              <span>Katalog</span>
              <strong>Lihat produk →</strong>
            </Link>
          </nav>
        </div>

        <div className="wallet-ledger__details">
          <MembershipSummary
            pending={isPending}
            active={data?.membership.active === true}
            expiresAt={data?.membership.expiresAt}
          />
          <div className="vault-breakdown">
            <div className="vault-breakdown__heading">
              <div>
                <h2>ASQ yang telah digunakan</h2>
                <p>Distribusi permanen per utility.</p>
              </div>
              <Link to="/protocol" className="linkline">
                Protocol →
              </Link>
            </div>
            {isPending ? (
              <Skeleton className="mt-6 h-40 w-full" />
            ) : (
              <VaultLedger utilities={data?.utilities ?? []} />
            )}
          </div>
        </div>
      </section>

      <dl className="wallet-tally">
        <Metric label="Voucher aktif" value={data?.voucherCount ?? 0} />
        <Metric label="Order" value={data?.orderCount ?? 0} />
        <Metric
          label="Total dibelanjakan"
          value={`${formatASQ(data?.spentRaw, { maxFractionDigits: 2 })} ASQ`}
        />
      </dl>

      <section className="activity-ledger">
        <header>
          <div>
            <h2>Aktivitas terakhir</h2>
            <p>Pembayaran utility terbaru dari wallet ini.</p>
          </div>
          <Link to="/activity" className="linkline">
            Semua aktivitas →
          </Link>
        </header>

        {isPending ? (
          <Skeleton className="mt-6 h-40 w-full" />
        ) : data?.activity?.length ? (
          <ol>
            {data.activity.map((entry) => (
              <li key={entry.id}>
                <div>
                  <time>{formatDate(entry.createdAt)}</time>
                  <p>{entry.label}</p>
                </div>
                <div>
                  <p className="datum">−{formatASQ(entry.amountRaw)} ASQ</p>
                  <Status state="ok">{entry.status}</Status>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="activity-ledger__empty">
            Belum ada pembayaran. Pilih membership, voucher, atau produk untuk memulai.
          </p>
        )}
      </section>
    </div>
  );
}

function MembershipSummary({
  pending,
  active,
  expiresAt,
}: {
  pending: boolean;
  active: boolean;
  expiresAt?: string;
}) {
  return (
    <section className={`membership-summary ${active ? "membership-summary--active" : ""}`}>
      <div>
        <p>Access pass</p>
        <h2>{active ? "Membership aktif" : "Membership belum aktif"}</h2>
      </div>
      {pending ? (
        <Skeleton className="h-12 w-44" />
      ) : active && expiresAt ? (
        <div className="membership-summary__expiry">
          <Status state="ok">Aktif</Status>
          <strong>{formatDate(expiresAt)}</strong>
          <span>{daysUntil(expiresAt)} hari tersisa</span>
        </div>
      ) : (
        <Link to="/membership" className="btn btn--primary">
          Aktifkan sekarang
        </Link>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="datum">{value}</dd>
    </div>
  );
}

function VaultLedger({ utilities }: { utilities: Utility[] }) {
  const values = utilities.map((utility) => BigInt(utility.lockedRaw || "0"));
  const largest = values.reduce((max, value) => (value > max ? value : max), 0n);

  if (utilities.length === 0) {
    return <p className="vault-breakdown__empty">Registry belum dapat dibaca.</p>;
  }

  return (
    <ul className="vault-breakdown__list">
      {utilities.map((utility, index) => {
        const value = values[index];
        const percent = largest === 0n ? 0 : Number((value * 100n) / largest);

        return (
          <li key={utility.name}>
            <div>
              <span>{utilityLabel(utility.name)}</span>
              <ASQAmount raw={utility.lockedRaw} size="sm" maxFractionDigits={2} />
            </div>
            <span className="vault-breakdown__track">
              <span
                style={{ width: `${Math.max(percent, value > 0n ? 2 : 0)}%` }}
                role="img"
                aria-label={`${utilityLabel(utility.name)}: ${formatASQ(utility.lockedRaw)} ASQ terkunci`}
              />
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 11) return "Selamat pagi";
  if (hour < 15) return "Selamat siang";
  if (hour < 19) return "Selamat sore";
  return "Selamat malam";
}
